import { useCallback, useEffect, useState } from 'react';

import { Admin } from './Admin';
import { api, type Session } from './api';
import { Client360 } from './Client360';
import { Clients } from './Clients';
import { CommissionCredits } from './CommissionCredits';
import { Contractor360 } from './Contractor360';
import { Portal } from './Portal';
import { Contractors } from './Contractors';
import { Docs } from './Docs';
import { Executive } from './Executive';
import { Login } from './Login';
import { Recruiter } from './Recruiter';
import { Recruiters } from './Recruiters';
import { Workspace } from './Workspace';
import { MyCommissions } from './MyCommissions';
import { WeeklyCommissionReport } from './WeeklyCommissionReport';
import { GpSummary } from './reports/GpSummary';
import { WeeklyScoreboard } from './reports/WeeklyScoreboard';
import { AdpPreview } from './reports/AdpPreview';
import { ExpensesReport } from './reports/ExpensesReport';
import { TimeFilterProvider } from './context/TimeFilterContext';

type Route =
  | { page: 'operations' }
  | { page: 'executive' }
  | { page: 'my-commissions' }
  | { page: 'credit-assignments' }
  | { page: 'weekly-commission' }
  | { page: 'gp-summary' }
  | { page: 'weekly-scoreboard' }
  | { page: 'expenses-report' }
  | { page: 'adp-preview' }
  | { page: 'clients' }
  | { page: 'contractors' }
  | { page: 'recruiters' }
  | { page: 'docs'; slug?: string }
  | { page: 'admin' }
  | { page: 'portal' }
  | { page: 'contractor'; id: string }
  | { page: 'client'; id: string }
  | { page: 'recruiter'; id: string };

/** Strip query-string portion from a hash segment (e.g. "uuid?planId=x" → "uuid") */
function stripQuery(s: string): string {
  const q = s.indexOf('?');
  return q >= 0 ? s.slice(0, q) : s;
}

function parseHash(): Route {
  const hash = window.location.hash.replace(/^#\/?/, '');
  if (hash === 'executive') return { page: 'executive' };
  if (hash === 'clients') return { page: 'clients' };
  if (hash === 'contractors') return { page: 'contractors' };
  if (hash === 'recruiters') return { page: 'recruiters' };
  if (hash === 'docs') return { page: 'docs' };
  if (hash === 'admin') return { page: 'admin' };
  if (hash === 'portal') return { page: 'portal' };
  if (hash === 'my-commissions') return { page: 'my-commissions' };
  if (hash === 'credit-assignments') return { page: 'credit-assignments' };
  if (hash === 'weekly-commission') return { page: 'weekly-commission' };
  if (hash === 'gp-summary') return { page: 'gp-summary' };
  if (hash === 'weekly-scoreboard') return { page: 'weekly-scoreboard' };
  if (hash === 'expenses-report') return { page: 'expenses-report' };
  if (hash === 'adp-preview') return { page: 'adp-preview' };
  if (hash.startsWith('docs/')) return { page: 'docs', slug: hash.slice(5) };
  if (hash.startsWith('contractor/')) return { page: 'contractor', id: stripQuery(hash.slice(11)) };
  if (hash.startsWith('client/')) return { page: 'client', id: stripQuery(hash.slice(7)) };
  if (hash.startsWith('recruiter/')) return { page: 'recruiter', id: stripQuery(hash.slice(10)) };
  return { page: 'operations' };
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [checked, setChecked] = useState(false);
  const [route, setRoute] = useState<Route>(parseHash);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = useCallback((hash: string) => {
    window.location.hash = hash;
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((resolved) => {
        if (!cancelled) setSession(resolved);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-profit-dim" aria-hidden />
      </div>
    );
  }

  if (session === null) {
    return <Login onSignedIn={setSession} />;
  }

  const onSignOut = () => {
    void api.logout().finally(() => {
      setSession(null);
      window.location.hash = '';
    });
  };

  const shell = { session, onSignOut, navigate, route };

  let page: React.JSX.Element;
  switch (route.page) {
    case 'executive':
      page = <Executive {...shell} />;
      break;
    case 'clients':
      page = <Clients {...shell} />;
      break;
    case 'contractors':
      page = <Contractors {...shell} />;
      break;
    case 'recruiters':
      page = <Recruiters {...shell} />;
      break;
    case 'docs':
      page = <Docs {...shell} docSlug={route.slug} />;
      break;
    case 'admin':
      page = <Admin {...shell} />;
      break;
    case 'portal':
      page = <Portal {...shell} />;
      break;
    case 'my-commissions':
      page = <MyCommissions {...shell} />;
      break;
    case 'credit-assignments':
      page = <CommissionCredits {...shell} />;
      break;
    case 'weekly-commission':
      page = <WeeklyCommissionReport {...shell} />;
      break;
    case 'gp-summary':
      page = <GpSummary {...shell} />;
      break;
    case 'weekly-scoreboard':
      page = <WeeklyScoreboard {...shell} />;
      break;
    case 'expenses-report':
      page = <ExpensesReport {...shell} />;
      break;
    case 'adp-preview':
      page = <AdpPreview {...shell} />;
      break;
    case 'contractor':
      page = <Contractor360 {...shell} contractorId={route.id} />;
      break;
    case 'client':
      page = <Client360 {...shell} clientId={route.id} />;
      break;
    case 'recruiter':
      page = <Recruiter {...shell} userId={route.id} />;
      break;
    default: {
      // Role-based default landing page
      const role = session.user.role;
      if (role === 'contractor') page = <Portal {...shell} />;
      else if (role === 'recruiter' || role === 'salesperson') page = <MyCommissions {...shell} />;
      else if (role === 'finance' || role === 'read_only') page = <Executive {...shell} />;
      else page = <Workspace {...shell} />;
    }
  }

  return <TimeFilterProvider>{page}</TimeFilterProvider>;
}
